import { IScheduledVisits } from "@home-health-notify/domain";
import {
  fetchRoute as fetchRouteFromCommons,
  getJitteredAddresses,
  IGMLatLng,
  IGMPolyline,
  IGMRoute,
  IGMRouteLeg,
  IGMRouteLegStep,
  IGMRoutesApiResponse,
  ILatLng,
  INFO_TYPE,
  makeRouteData,
  reorderLocationsBasedOnOptimization,
  sortBySchedulerTime,
} from "hhn-commons";
import config from "./Config";

export const GOOGLE_MAPS_API_KEY = config.googleMapKey;

/**
 * Wrapper around commons fetchRoute that uses the web app's API key
 */
export const fetchRoute = async ({
  locations,
  isOptimize = false,
  originLocation,
  destinationLocation,
}: {
  locations: google.maps.LatLngLiteral[];
  isOptimize?: boolean;
  originLocation?: google.maps.LatLngLiteral;
  destinationLocation?: google.maps.LatLngLiteral;
}): Promise<IGMRoute | undefined> => {
  // Convert google.maps.LatLngLiteral to ILatLng
  const locationsAsILatLng: ILatLng[] = locations.map(loc => ({
    lat: loc.lat,
    lng: loc.lng,
  }));

  const originAsILatLng: ILatLng | undefined = originLocation
    ? { lat: originLocation.lat, lng: originLocation.lng }
    : undefined;

  const destinationAsILatLng: ILatLng | undefined = destinationLocation
    ? { lat: destinationLocation.lat, lng: destinationLocation.lng }
    : undefined;

  return fetchRouteFromCommons({
    locations: locationsAsILatLng,
    isOptimize,
    originLocation: originAsILatLng,
    destinationLocation: destinationAsILatLng,
    apiKey: GOOGLE_MAPS_API_KEY ?? "",
  });
};

/**
 * Web-specific: Fits the map bounds to include all markers
 * This uses google.maps.LatLngBounds which is web-only
 */
export const fitMapToMarkers = (
  map: google.maps.Map | null,
  markers: IScheduledVisits[],
) => {
  if (!map || !markers?.length) return;

  const bounds = new google.maps.LatLngBounds();
  markers.forEach(marker => {
    if (!marker.location || !Array.isArray(marker.location.coordinates)) return;
    const [lng, lat] = marker.location.coordinates;
    bounds.extend(new google.maps.LatLng(lat, lng));
  });

  map.fitBounds(bounds, 58);
};

/**
 * A route with no custom end location is a round trip, so the destination
 * carries the origin's exact coordinate. Drawing an endpoint marker at both
 * leaves two pins fighting over the same pixel.
 */
export const hasDistinctDestination = (
  origin?: google.maps.LatLngLiteral,
  destination?: google.maps.LatLngLiteral,
): boolean =>
  !!destination &&
  (!origin || destination.lat !== origin.lat || destination.lng !== origin.lng);

// Re-export utilities from commons for backward compatibility
export {
  getJitteredAddresses,
  makeRouteData,
  reorderLocationsBasedOnOptimization,
  sortBySchedulerTime,
  INFO_TYPE,
};

// Re-export types from commons
export type {
  IGMLatLng,
  IGMPolyline,
  IGMRouteLegStep,
  IGMRouteLeg,
  IGMRoute,
  IGMRoutesApiResponse,
};
